import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { poolServiceProviderInfoService } from '../../../../services/poolServiceProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('PoolServiceProviderData');

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const PoolServiceProviderDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<{ params: RouteParams }, 'params'>>();
  const { targetUserId, viewOnly } = route.params || {};
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();
  const [poolServiceProviderData, setPoolServiceProviderData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
  const [showAccountNumber, setShowAccountNumber] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadPoolServiceProviderData();
  }, []);

  const loadPoolServiceProviderData = async () => {
    try {
      setLoading(true);
      const data = await poolServiceProviderInfoService.getAllPoolServiceProviderInfo(targetUserId);
      
      if (data && data.length > 0) {
        // Skip loading portal credentials in viewOnly mode - they are protected until EOL
        if (!viewOnly || isDeathVerifiedForSelectedUser) {
          // Load portal credentials for each pool service provider
          const updatedData = await Promise.all(
            data.map(async (poolServiceProvider) => {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for pool service provider ID:', poolServiceProvider.id);
                const portalData = await vaultService.getPortalCredentials(poolServiceProvider.id, 'pool-service-provider');
                logger.debug('[Portal Debug] Portal data retrieved for pool service provider:', poolServiceProvider.id, portalData);
                if (portalData) {
                  poolServiceProvider.username = portalData.username;
                  poolServiceProvider.password = portalData.password;
                  poolServiceProvider.accountNumber = portalData.accountNumber;
                  logger.debug('[Portal Debug] Assigned credentials to pool service provider data');
                } else {
                  logger.debug('[Portal Debug] No portal data returned for pool service provider:', poolServiceProvider.id);
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for pool service provider:', poolServiceProvider.id, vaultError);
              }
              return poolServiceProvider;
            })
          );

          logger.debug('[Data Debug] Final pool service provider data being set:', updatedData.length, 'providers');
          logger.debug('[Data Debug] First provider account number:', updatedData[0]?.accountNumber);
          setPoolServiceProviderData(updatedData);
        } else {
          setPoolServiceProviderData(data);
        }
      }
    } catch (error) {
      logger.error('Error loading pool service provider data:', error);
      Alert.alert('Error', 'Failed to load pool service provider information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: string | number) => {
    if (!amount) return '';
    if (typeof amount === 'number') {
      return `$${amount.toFixed(2)}`;
    }
    return amount.toString();
  };

  const formatDate = (date: string) => {
    if (!date) return '';
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return date;
    }
  };

  const handleEditPoolServiceProvider = (poolServiceProvider: any) => {
    navigation.navigate('PoolServiceProviderInput', { 
      poolServiceProviderId: poolServiceProvider.id, 
      isEdit: true 
    });
  };

  const handleDeletePoolServiceProvider = async (poolServiceProvider: any) => {
    Alert.alert('Delete', 'Are you sure you want to delete this pool service provider information?', [
      { text: 'Cancel', style: 'cancel' },
      { 
        text: 'Delete', 
        style: 'destructive', 
        onPress: async () => {
          try {
            await poolServiceProviderInfoService.deletePoolServiceProviderInfo(poolServiceProvider.id);
            
            // Reload data after deletion
            await loadPoolServiceProviderData();
            
            Alert.alert('Deleted', 'Pool service provider information deleted.');
          } catch (e) {
            logger.error('Delete pool service provider info failed:', e);
            Alert.alert('Error', 'Failed to delete pool service provider information');
          }
        } 
      },
    ]);
  };

  const handleAddPoolServiceProvider = () => {
    navigation.navigate('PoolServiceProviderInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Pool Service Provider Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading pool service provider information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!poolServiceProviderData || poolServiceProviderData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Pool Service Provider Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly
                ? "This user has not added pool service provider information yet."
                : "No pool service provider information found"}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddPoolServiceProvider}
              >
                <Text style={styles.addButtonText}>Add Pool Service Provider Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Pool Service Provider Information</Text>

      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {poolServiceProviderData.map((poolServiceProvider, index) => (
            <View key={poolServiceProvider.id || index} style={styles.poolServiceProviderCard}>
              <View style={styles.poolServiceProviderHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.poolServiceProviderName}>
                    {poolServiceProvider.companyName || 'Unknown Provider'}
                  </Text>
                  <Text style={styles.poolServiceProviderType}>
                    {poolServiceProvider.serviceFrequency || 'Not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editPoolServiceProviderButton}
                      onPress={() => handleEditPoolServiceProvider(poolServiceProvider)}
                    >
                      <Text style={styles.editPoolServiceProviderButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deletePoolServiceProviderButton}
                      onPress={() => handleDeletePoolServiceProvider(poolServiceProvider)}
                    >
                      <Text style={styles.deletePoolServiceProviderButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Company Name" value={poolServiceProvider.companyName || ''} />
              <InfoItem label="Technician Name" value={poolServiceProvider.technicianName || ''} />
              <InfoItem label="Phone Number" value={formatPhoneNumber(poolServiceProvider.phoneNumber) || ''} />
              <InfoItem label="Emergency Phone" value={formatPhoneNumber(poolServiceProvider.emergencyPhone) || ''} />
              <InfoItem label="Email" value={poolServiceProvider.email || ''} />
              <InfoItem label="Service Address" value={poolServiceProvider.serviceAddress || ''} />
              <InfoItem label="Services Provided" value={poolServiceProvider.servicesProvided || ''} />
              <InfoItem label="Service Frequency" value={poolServiceProvider.serviceFrequency || ''} />
              <InfoItem label="Cost per Service" value={formatCurrency(poolServiceProvider.costPerService) || ''} />
              <InfoItem label="Seasonal Service" value={poolServiceProvider.seasonalService ? 'Yes' : 'No'} />
              {poolServiceProvider.seasonalService && (
                <>
                  <InfoItem label="Season Start Date" value={formatDate(poolServiceProvider.seasonStartDate) || ''} />
                  <InfoItem label="Season End Date" value={formatDate(poolServiceProvider.seasonEndDate) || ''} />
                </>
              )}
              <InfoItem label="Pool Type" value={poolServiceProvider.poolType || ''} />
              <InfoItem label="Pool Size" value={poolServiceProvider.poolSize || ''} />
              <InfoItem label="Equipment Maintained" value={poolServiceProvider.equipmentMaintained || ''} />
              <InfoItem label="Chemicals Used" value={poolServiceProvider.chemicalsUsed || ''} />
              <InfoItem label="Emergency Service" value={poolServiceProvider.emergencyService ? 'Yes' : 'No'} />
              
              {poolServiceProvider.accountNumber && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Account Number 🔒</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showAccountNumber[poolServiceProvider.id] ? poolServiceProvider.accountNumber : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowAccountNumber(prev => ({ ...prev, [poolServiceProvider.id]: !prev[poolServiceProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showAccountNumber[poolServiceProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {poolServiceProvider.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUsername[poolServiceProvider.id] ? poolServiceProvider.username : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUsername(prev => ({ ...prev, [poolServiceProvider.id]: !prev[poolServiceProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUsername[poolServiceProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {poolServiceProvider.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showPassword[poolServiceProvider.id] ? poolServiceProvider.password : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowPassword(prev => ({ ...prev, [poolServiceProvider.id]: !prev[poolServiceProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showPassword[poolServiceProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Notes" value={poolServiceProvider.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherPoolServiceProviderButton}
                onPress={handleAddPoolServiceProvider}
              >
                <Text style={styles.addAnotherPoolServiceProviderButtonText}>Add Another Pool Service Provider</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  poolServiceProviderCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  poolServiceProviderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  poolServiceProviderName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  poolServiceProviderType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editPoolServiceProviderButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editPoolServiceProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deletePoolServiceProviderButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deletePoolServiceProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherPoolServiceProviderButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherPoolServiceProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});